import { Component, type ReactNode, createRef } from 'react';
import { cardDeReferencia, mesmaOrdem } from '../lista-parada';

interface Props {
  /** Ligada só na ordenação que mantém a posição. Desligada, é uma div comum. */
  ligada: boolean;
  /** Mudou (troca de aba): a lista é outra, não há lugar pra manter. */
  chave: string;
  /** Ids na ordem exibida. */
  ids: readonly number[];
  className?: string;
  children: ReactNode;
}

interface Referencia {
  id: number;
  y: number;
}

/**
 * Caixa de rolagem que mantém a tela parada quando um card sobe: a conversa vai
 * pro topo e quem está rolado continua vendo os mesmos cards, igual ao WhatsApp.
 * No topo da lista não segura nada, então a conversa aparece em cima.
 *
 * Sem isto o navegador leva a tela junto: o React deixa no lugar o card que
 * subiu e move os de cima pra baixo dele, e a âncora de rolagem acompanha o
 * card. Por isso desliga a âncora e faz a conta à mão.
 *
 * É classe porque precisa medir a tela ANTES de o React mexer nos cards
 * (`getSnapshotBeforeUpdate`), e isso não existe em hook.
 */
export class ListaParada extends Component<Props> {
  private readonly caixa = createRef<HTMLDivElement>();

  override getSnapshotBeforeUpdate(anterior: Props): Referencia | null {
    const { ligada, chave, ids } = this.props;
    const caixa = this.caixa.current;
    if (!ligada || !caixa || anterior.chave !== chave || caixa.scrollTop <= 0) return null;
    if (mesmaOrdem(anterior.ids, ids)) return null;

    const topo = caixa.getBoundingClientRect().top;
    const fundo = topo + caixa.clientHeight;
    const naTela = new Map<number, number>();
    for (const el of caixa.querySelectorAll<HTMLElement>('[data-ticket-id]')) {
      const r = el.getBoundingClientRect();
      if (r.top >= fundo) break;
      if (r.bottom > topo) naTela.set(Number(el.dataset.ticketId), r.top - topo);
    }
    const id = cardDeReferencia([...naTela.keys()], anterior.ids, ids);
    return id === null ? null : { id, y: naTela.get(id) ?? 0 };
  }

  override componentDidUpdate(_props: Props, _state: unknown, referencia: Referencia | null): void {
    const caixa = this.caixa.current;
    if (!referencia || !caixa) return;
    const el = caixa.querySelector(`[data-ticket-id="${referencia.id}"]`);
    if (!el) return;
    const agora = el.getBoundingClientRect().top - caixa.getBoundingClientRect().top;
    caixa.scrollTop += agora - referencia.y;
  }

  override render(): ReactNode {
    const { ligada, className, children } = this.props;
    return (
      <div
        ref={this.caixa}
        className={className}
        style={ligada ? { overflowAnchor: 'none' } : undefined}
      >
        {children}
      </div>
    );
  }
}
